import * as React from 'react';

import AqButton from '../../Aq/Button';
import AqSmileRating from '../../Aq/SmileRating'

import GenModal from '../../Gen/Modal/component';

interface DocAiAssistantThreadFeedbackModalProps {
  threadId: number
  experienceQuestion?: string;
  responseOptions: Array<string>;
  continueButtonTitle?: string;
  onContinue: () => void;
  onCancel: () => void;
}

async function submitFeedback(threadId: number, rating: number, issues: Array<string>, comment: string) {
  const response = await fetch(`/ai_assistant/threads/${threadId}/feedbacks`, {
    method: "POST",
    headers: ReactOnRails.authenticityHeaders({
      'Accept': "application/json",
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      feedback: {
        smiley_rating: rating,
        issues: issues,
        comment: comment
      }
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ errors: ['Failed to submit feedback'] }));
    throw new Error(errorData.errors?.[0] || 'Failed to submit feedback');
  }

  return response;
}

export default function DocAiAssistantThreadFeedbackModal(props: DocAiAssistantThreadFeedbackModalProps) {
  const {
    threadId,
    experienceQuestion,
    responseOptions,
    continueButtonTitle = "CONTINUE",
    onContinue,
    onCancel,
  } = props;

  const [rating, setRating] = React.useState<number>()
  const [responses, setResponses] = React.useState<Array<string>>([])
  const [comment, setComment] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  const canContinue = typeof rating === "number" && !isSubmitting

  return (
    <GenModal open>
      <div
        className="gen-modal-panel"
        style={{
          maxWidth: "400px",
        }}
      >
        <div
          style={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: "16px",
            paddingBottom: "3px"
          }}
        >
          {experienceQuestion || "Let us know about your experience:"} (required)
        </div>
        <div
          className="rating-options"
          style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            background: "#F3F3F3"
          }}
        >
          <AqSmileRating
            value={rating}
            onChange={(newRating) => setRating(newRating)}
          />
        </div>
        <div
          style={{
          fontFamily: "Roboto Condensed",
          fontWeight: 700,
          fontSize: "12px",
          textTransform: "uppercase",
          color: "#1B5599",
          paddingTop: "10px"
        }}>
          Select all that apply (optional)
        </div>
        <div
          style={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            paddingTop: "10px"
          }}
        >
          {responseOptions.map((responseOption) => (
            <label
              key={responseOption}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <input
                type="checkbox"
                className="issue-checkbox"
                checked={responses.includes(responseOption)}
                onChange={(e) => setResponses(e.target.checked ? [...responses, responseOption] : responses.filter((option) => option !== responseOption)) }
              />
              <span>{responseOption}</span>
            </label>
          ))}
        </div>
        <div
          style={{
            fontFamily: "Roboto Condensed",
            fontWeight: 700,
            fontSize: "12px",
            textTransform: "uppercase",
            color: "#1B5599",
            paddingTop: "10px"
          }}>
          How could this experience be improved? (optional)
        </div>
        <textarea
          id="feedback-comment"
          placeholder=""
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}

          style={{
            width: "100%",
            height: "80px",
            overflow: "auto",
            resize: "vertical",
            padding: "5px",
            fontFamily: "Roboto",
            fontSize: "12px",
            border: "1px solid #ccc",
            borderRadius: "4px"
          }}
        />
        <div
          style={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: "12px",
            paddingTop: "5px"
          }}>
          If you have an issue that requires immediate assistance, please <a href="https://aquifer.org/knowledge/support/aquifer-support" target="_blank">contact support</a>.
        </div>
        {submitError && (
          <div style={{ color: 'red', fontSize: '12px', marginTop: '10px' }}>
            {submitError}
          </div>
        )}
        <div
          className="controls"
          style={{
            display: "flex",
            float: "right",
            gap: "10px"
          }}
        >
          <AqButton
            onClick={async () => {
              if (isSubmitting) return;

              setIsSubmitting(true);
              setSubmitError(null);

              try {
                await submitFeedback(threadId, rating, responses, comment);
                onContinue();
              } catch (_error) {
                setSubmitError('Failed to submit feedback. Please try again.');
                setIsSubmitting(false);
              }
            }}
            disabled={!canContinue}
          >
            {isSubmitting ? 'SUBMITTING...' : continueButtonTitle}
          </AqButton>
          <AqButton
            onClick={onCancel}
            outline
            disabled={isSubmitting}
          >
            Go Back
          </AqButton>
        </div>
      </div>
    </GenModal>
  )
}

{/* Can use AqSmileRating???? */}
